import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import IconButton from '../../../common/IconButton';
import CustomDatePicker from '../../../common/CustomDatePicker';
import { list as listCompetitions } from '../../../../lib/api/competition';
import { list as listCompetitionClubs } from '../../../../lib/api/competitionClubs';
import { listCompetitionRounds } from '../../../../lib/api/match';

const getClubEntityId = item => {
  return (
    item?.entities_id ??
    item?.club_entities_id ??
    item?.clubs_entities_id ??
    null
  );
};

const SearchAccordion = ({ handleSearch, showClubFilters = true, showActiveFilter = true }) => {
  const [competitions, setCompetitions] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [search, setSearch] = useState({});
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedHomeClub, setSelectedHomeClub] = useState(null);
  const [selectedAwayClub, setSelectedAwayClub] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await listCompetitions();
        setCompetitions(
          (response || []).map(item => ({
            value: item.competitions_id,
            label: item.name,
            raw: item
          }))
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja takmicenja:', error);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchDependencies = async () => {
      if (!selectedCompetition?.raw) {
        setRounds([]);
        setClubs([]);
        setSelectedRound(null);
        setSelectedHomeClub(null);
        setSelectedAwayClub(null);
        setSearch(prev => {
          const next = { ...prev };
          delete next.competitions_id;
          delete next.competition_round_id;
          delete next.club_entities_id_1;
          delete next.club_entities_id_2;
          return next;
        });
        return;
      }

      setSearch(prev => ({
        ...prev,
        competitions_id: selectedCompetition.value
      }));

      try {
        const [roundsResponse, clubsResponse] = await Promise.all([
          listCompetitionRounds({
            competitions_id: selectedCompetition.value
          }),
          listCompetitionClubs(selectedCompetition.raw.competitions_guid)
        ]);

        setRounds(
          (roundsResponse || []).map(item => ({
            value: item.competition_round_id,
            label: item.match_round_name,
            raw: item
          }))
        );

        setClubs(
          (clubsResponse || [])
            .map(item => ({
              value: getClubEntityId(item),
              label: item.clubs_name || item.name,
              raw: item
            }))
            .filter(item => item.value !== null)
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja zavisnih filtera:', error);
        setRounds([]);
        setClubs([]);
      }
    };

    fetchDependencies();
  }, [selectedCompetition]);

  const selectedClubOptions = useMemo(() => clubs, [clubs]);

  const handleDateChange = (_, date) => {
    setSelectedDate(date);
    setSearch(prev => {
      const next = { ...prev };
      if (!date) {
        delete next.date;
      } else {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        next.date = `${year}-${month}-${day}`;
      }
      return next;
    });
  };

  return (
    <Accordion defaultActiveKey={null}>
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <FontAwesomeIcon icon="search" className="fs-10 me-2" />
          <strong>Pretraga</strong>
        </Accordion.Header>
        <Accordion.Body>
          <Form.Group as={Col} md="12">
            <Row className="flex-between-center mb-3">
              <Col md={4}>
                <Form.Label>Takmičenja</Form.Label>
                <Select
                  options={competitions}
                  value={selectedCompetition}
                  classNamePrefix="react-select"
                  placeholder="Pretraži takmičenje..."
                  onChange={option => setSelectedCompetition(option || null)}
                  isClearable
                />
              </Col>
              <Col md={4}>
                <Form.Label>Kolo</Form.Label>
                <Select
                  options={rounds}
                  value={selectedRound}
                  classNamePrefix="react-select"
                  placeholder="Pretraži kolo..."
                  onChange={option => {
                    setSelectedRound(option || null);
                    setSearch(prev => {
                      const next = { ...prev };
                      if (!option) delete next.competition_round_id;
                      else next.competition_round_id = option.value;
                      return next;
                    });
                  }}
                  isClearable
                  isDisabled={!selectedCompetition}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Datum</Form.Label>
                <CustomDatePicker
                  name="date"
                  selectedDate={selectedDate}
                  onChange={handleDateChange}
                  withPortal={false}
                />
              </Col>
            </Row>

            {(showClubFilters || showActiveFilter) && (
              <Row className="mb-3">
              {showClubFilters && (
                <>
                  <Col md={4}>
                    <Form.Label>Klub</Form.Label>
                    <Select
                      options={selectedClubOptions}
                      value={selectedHomeClub}
                      classNamePrefix="react-select"
                      placeholder="Pretraži domaćina..."
                      onChange={option => {
                        setSelectedHomeClub(option || null);
                        setSearch(prev => {
                          const next = { ...prev };
                          if (!option) delete next.club_entities_id_1;
                          else next.club_entities_id_1 = option.value;
                          return next;
                        });
                      }}
                      isClearable
                      isDisabled={!selectedCompetition}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Gost</Form.Label>
                    <Select
                      options={selectedClubOptions}
                      value={selectedAwayClub}
                      classNamePrefix="react-select"
                      placeholder="Pretraži gosta..."
                      onChange={option => {
                        setSelectedAwayClub(option || null);
                        setSearch(prev => {
                          const next = { ...prev };
                          if (!option) delete next.club_entities_id_2;
                          else next.club_entities_id_2 = option.value;
                          return next;
                        });
                      }}
                      isClearable
                      isDisabled={!selectedCompetition}
                    />
                  </Col>
                </>
              )}
              {showActiveFilter && (
                <Col md={4}>
                  <Form.Label>Aktivan</Form.Label>
                  <Form.Select
                    onChange={event => {
                      const { value } = event.target;
                      setSearch(prev => {
                        const next = { ...prev };
                        if (value === '') delete next.is_active;
                        else next.is_active = value;
                        return next;
                      });
                    }}
                  >
                    <option value="">Sve</option>
                    <option value="1">Da</option>
                    <option value="0">Ne</option>
                  </Form.Select>
                </Col>
              )}
              </Row>
            )}

            <Row className="flex-between-center mb-3">
              <Col md="12" className="text-end mt-2">
                <IconButton
                  className="rounded-pill me-1 mb-1"
                  variant="falcon-primary"
                  icon="search"
                  transform="shrink-3"
                  onClick={() => handleSearch(search)}
                >
                  Pretraga
                </IconButton>
              </Col>
            </Row>
          </Form.Group>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SearchAccordion;
